/**
 * <analytics-content> reports editorial links on homepage, page, blog and article
 * templates. The hidden controller observes #MainContent without changing layout.
 * Product lists own select_item; navigation, forms, drawers and in-page actions are
 * excluded. Section/block identity comes from Liquid data-* attributes.
 *
 * Same-tab, same-origin navigation uses @theme/analytics's persisted delivery so
 * GA4 keeps the originating page. New-tab and external links publish immediately
 * because the destination may not load this theme or share sessionStorage.
 * Contract: GA4-EVENTS-PLAN.md section 5.6; existing custom pixel subscription.
 */

import { EVENTS, trackEvent, trackNavigationEvent } from '@theme/analytics';

const EXCLUDED = [
  'product-card',
  '[data-list-item]',
  '[data-action]',
  '[download]',
  '[role="button"]',
  '[aria-disabled="true"]',
  'form',
  'header-component',
  'footer-component',
  'drawer-component',
  'dialog',
  'lookbook-tabs',
  '.collection-item--has-look',
  '.stl-popup',
  '.pdp-dropdown',
  '.pdp-description',
  '.pdp-connected-colors',
  '.footer-localization__option',
  '.article-breadcrumbs',
  '.breadcrumbs',
].join(',');

function linkText(link) {
  const text = (value) => value?.replace(/\s+/g, ' ').trim();
  // Buttons render the same label twice for their hover animation.
  const buttonText = link.querySelector('.button-txt')?.textContent;
  const labelledBy = link
    .getAttribute('aria-labelledby')
    ?.split(/\s+/)
    .map((id) => document.getElementById(id)?.textContent || '')
    .join(' ');
  return (
    text(buttonText) ||
    text(link.getAttribute('aria-label')) ||
    text(labelledBy) ||
    text(link.textContent) ||
    text(link.querySelector('img[alt]')?.alt) ||
    text(link.getAttribute('title')) ||
    ''
  );
}

function linkType(link) {
  if (link.classList.contains('Button')) return 'button';
  if (
    link.matches(
      '.collection-item__link, .banner__link, .blog-item__overlay-link, .image-text-media__link'
    )
  ) {
    return 'tile';
  }
  return 'text';
}

class AnalyticsContent extends HTMLElement {
  #main;
  #onClick = (event) => {
    if (event.defaultPrevented) return;
    if (event.type === 'auxclick' ? event.button !== 1 : event.button !== 0) return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || !this.#main?.contains(link) || link.closest(EXCLUDED)) return;
    // Explore More is editorial collection discovery. Other nav elements are UI.
    if (link.closest('nav, [role="navigation"]') && !link.closest('usp-collection-links')) return;
    if (link.closest('.collection-item')?.querySelector('shop-the-look, .stl-popup')) return;

    const href = link.getAttribute('href')?.trim();
    if (!href || href.startsWith('#')) return;
    let url;
    try {
      url = new URL(href, document.baseURI);
    } catch {
      return;
    }
    if (!['http:', 'https:'].includes(url.protocol)) return;
    const sameOrigin = url.origin === location.origin;
    if (sameOrigin && url.pathname === location.pathname && url.search === location.search) return;

    const target = (link.getAttribute('target') || '').toLowerCase();
    const staysOnPage =
      event.button === 1 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (target && !['_self', '_top', '_parent'].includes(target));
    const payload = {
      link_text: linkText(link),
      link_url: url.href,
      link_type: linkType(link),
      block_id: link.closest('[data-block-id]')?.dataset.blockId,
    };

    const track = sameOrigin && !staysOnPage ? trackNavigationEvent : trackEvent;
    track(EVENTS.contentLinkClick, payload, link);
  };

  connectedCallback() {
    this.#main = document.getElementById('MainContent');
    // Window's bubble phase follows the theme's document-level delegates, so
    // cancelled tabs/swipes cannot be counted, including native browser clicks.
    window.addEventListener('click', this.#onClick);
    window.addEventListener('auxclick', this.#onClick);
  }

  disconnectedCallback() {
    window.removeEventListener('click', this.#onClick);
    window.removeEventListener('auxclick', this.#onClick);
    this.#main = null;
  }
}

if (!customElements.get('analytics-content')) {
  customElements.define('analytics-content', AnalyticsContent);
}
